// One held station action (DECISIONS D-024): why it's held, what it says happened and when,
// and the manager's decision — apply it (it runs now, dated when it happened, as the device
// it came from; audited) or discard it with a note (audited). A decision is final.
//
// Before applying, the dialog shows what applying would do: the server runs it and rolls
// it back (review UI3A-1) — the stock it moves, the levels it leaves, and warnings (below
// zero, a later count of that stock, a lot still unknown, a missing permission). Failures
// are reported in the dialog, in words, with the server's text under "Technical detail";
// after one, the preview is read again and shown as it stands (UI3A-202). A later count or
// stock going below zero must be acknowledged before applying (UI3A-203). The dialog never
// outgrows the screen: only its body scrolls (UI3A-201).
//
// A held delivery (D-066: never refused) can be corrected before it is booked: where it
// was put, a line's product, a new lot for a line whose lot id another product has. The
// preview is read again with the corrections, and applying sends them.

import type { HeldCommandDetail, HoldPreview, ReceiptCorrections } from '@enp/domain/api';
import {
  Badge,
  Button,
  Checkbox,
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogForm,
  DialogHeader,
  DialogTitle,
  FormField,
  Input,
  LoadingSkeleton,
  PageHeader,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Skeleton,
  StatusBadge,
  Textarea,
  formatDate,
  toast,
} from '@enp/ui';
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';
import { z } from 'zod';
import { ArrowLeft, Check, CornerDownRight, RotateCcw, Trash2 } from 'lucide-react';
import {
  useDeferredValue,
  useEffect,
  useRef,
  useState,
  type FormEvent,
  type ReactNode,
} from 'react';
import { productQuery } from '../../../../catalogue/queries.js';
import { countedText } from '../../../../purchasing/figures.js';
import { productSearchQuery } from '../../../../purchasing/queries.js';
import { locationsQuery } from '../../../../stock/queries.js';
import { ApiError } from '../../../../lib/api.js';
import { AccountDate, useAccountTimeZone } from '../../../../shell/account-date.js';
import { useDocumentTitle } from '../../../../shell/page.js';
import { ErrorPanel } from '../../../../shell/route-states.js';
import {
  HOLD_REASON,
  HOLD_STATUS,
  commandLabel,
  deviceOf,
  describeCommand,
} from '../../../../stations/labels.js';
import {
  DeviceName,
  NoDeviceAccess,
  Notice,
  SettingsBreadcrumb,
  SettingsFrame,
  TechnicalDetail,
  useCanManageDevices,
} from '../../../../stations/page-parts.js';
import {
  holdPreviewQuery,
  holdQuery,
  invalidateStations,
  resolveHold,
} from '../../../../stations/queries.js';
import {
  LATE_MS,
  acknowledgement,
  applyFailure,
  appliedAdvice,
  codeWords,
  datedSentence,
  durationText,
  effectDelta,
  effectRows,
  leavesText,
  offsetText,
  flagWords,
  lateBy,
  problemWords,
  warningWords,
} from '../../../../stations/words.js';

/**
 * Opened from a purchase order or a receipt (UI8C-8): the order to go back to, since held
 * actions live under Settings.
 */
const heldActionSearchSchema = z.object({
  po: z.uuid().optional().catch(undefined),
  po_number: z.string().max(40).optional().catch(undefined),
});

export const Route = createFileRoute('/_app/settings/held-actions/$holdId')({
  validateSearch: heldActionSearchSchema,
  component: HeldActionPage,
});

/** "Back to PO-SRV-00003", when the page was opened from that order. */
function BackToOrder() {
  const { po, po_number } = Route.useSearch();
  if (!po) return null;
  return (
    <Link
      to="/purchasing/orders/$orderId"
      params={{ orderId: po }}
      className="inline-flex w-fit items-center gap-1 text-sm font-medium text-primary-text underline-offset-2 hover:underline"
    >
      <ArrowLeft aria-hidden className="size-4" />
      Back to {po_number ?? 'the purchase order'}
    </Link>
  );
}

/** What the action says happened, in one sentence. */
function whatHappened(hold: HeldCommandDetail): string {
  const raw = hold.raw as { payload?: unknown } | null;
  return describeCommand(hold.type, raw?.payload, hold.refs);
}

/** Reasons whose explanation already says the action can only be discarded. */
const DISCARD_ONLY_EXPLAINED = new Set(['invalid_envelope', 'command_id_conflict']);

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="grid gap-1 sm:grid-cols-[12rem_1fr]">
      <dt className="text-sm font-medium text-muted">{label}</dt>
      <dd className="text-body text-fg">{children}</dd>
    </div>
  );
}

// ---------------------------------------------------------------- the apply preview

const CAN_APPLY = new Set<HoldPreview['outcome']>([
  'applied',
  'applied_with_flags',
  'already_recorded',
]);

/** What applying would do, in words: the stock it moves, and what to check first. */
function PreviewBody({
  preview,
  correctable = false,
  ack,
}: {
  preview: HoldPreview;
  /** The risk acknowledgement: right under the warnings it answers (B-041). */
  ack?: ReactNode;
  /** A held delivery a manager can correct here (D-066): say so, don't send them away. */
  correctable?: boolean;
}) {
  const timeZone = useAccountTimeZone();
  const refusal = preview.refusal
    ? codeWords(preview.refusal.code, {
        title: 'It can’t be applied',
        explain: 'The server won’t apply it as it is.',
      })
    : null;
  const technical = preview.refusal
    ? [preview.refusal.code, preview.refusal.message].filter(Boolean).join(': ')
    : null;

  if (preview.outcome === 'still_rejected' && correctable) {
    return (
      <Notice
        tone="warning"
        title={`It can’t be booked as sent: ${refusal?.title.toLowerCase()}`}
        technical={technical}
      >
        {refusal?.explain} Correct it below, then apply.
      </Notice>
    );
  }
  if (preview.outcome === 'still_rejected') {
    return (
      <Notice
        tone="danger"
        title={`It still can’t be recorded: ${refusal?.title.toLowerCase()}`}
        technical={technical}
      >
        {refusal?.explain} It stays here: apply it once that is sorted out, or discard it with a
        note.
      </Notice>
    );
  }
  if (preview.outcome === 'rejected') {
    return (
      <Notice
        tone="danger"
        title={`The server would refuse it: ${refusal?.title.toLowerCase()}`}
        technical={technical}
      >
        {refusal?.explain} Nothing would be recorded, so there is nothing to apply: discard it with
        a note instead.
      </Notice>
    );
  }
  if (preview.outcome === 'cannot_apply') {
    return (
      <Notice tone="danger" title={refusal?.title ?? 'It can’t be applied'} technical={technical}>
        {refusal?.explain}
      </Notice>
    );
  }
  if (preview.outcome === 'already_recorded') {
    return (
      <Notice tone="neutral" title="It is already recorded">
        The station sent it again later and it went through. Applying only closes this, with your
        note; nothing changes twice.
      </Notice>
    );
  }
  // The risks first, with the tick they ask for; then what it changes (B-041).
  return (
    <div className="flex flex-col gap-3">
      {preview.warnings.map((w, i) => {
        const words = warningWords(w, preview, timeZone);
        return (
          <Notice key={`${w.code}-${i}`} tone="warning" title={words.title}>
            {words.explain}
          </Notice>
        );
      })}
      {ack}
      <section aria-labelledby="changes" className="flex flex-col gap-2">
        <h3 id="changes" className="text-sm font-semibold text-fg">
          What it changes
        </h3>
        {preview.effects.length === 0 ? (
          <p className="text-body text-muted">It changes no stock.</p>
        ) : (
          <ul className="flex flex-col divide-y divide-border rounded-md border border-border">
            {/* One item per stock row: an offset at a later count sits under the movement
                it offsets, and the level the row is left at is said once (UI8B-3). */}
            {effectRows(preview.effects).map((row) => (
              <li key={row.key} className="flex flex-col gap-1 px-3 py-2 text-body">
                <span className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-0.5">
                  <span className="text-fg">{row.place}</span>
                  <span className="tabular-nums text-fg">
                    {row.own.map(effectDelta).join(', ')}
                    {row.offsets.length === 0 &&
                      `${row.own.length > 0 ? ', ' : ''}${leavesText(row.levelAfter)}`}
                  </span>
                </span>
                {row.offsets.map((o, i) => (
                  <span
                    key={i}
                    className="flex items-start gap-1.5 pl-4 text-sm tabular-nums text-muted"
                  >
                    <CornerDownRight aria-hidden className="mt-0.5 size-3.5 shrink-0" />
                    {offsetText(o, timeZone)}
                  </span>
                ))}
                {row.offsets.length > 0 && (
                  <span className="self-end tabular-nums text-fg">
                    {leavesText(row.levelAfter).replace(/^l/, 'L')}
                  </span>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

// ---------------------------------------------------------------- correcting a delivery

interface DeliveryLine {
  line_id: string;
  product_id?: string;
  qty: string;
  uom: string;
  lot?: { lot_code?: string };
}

/** The corrections as the API takes them, or undefined when there are none. */
function correctionsOf(
  location: string,
  products: Record<string, { id: string; name: string }>,
  newLots: Record<string, boolean>,
): ReceiptCorrections | undefined {
  const ids = new Set([
    ...Object.keys(products),
    ...Object.keys(newLots).filter((k) => newLots[k]),
  ]);
  const lines = [...ids].map((line_id) => ({
    line_id,
    ...(products[line_id] ? { product_id: products[line_id].id } : {}),
    ...(newLots[line_id] ? { new_lot: true as const } : {}),
  }));
  if (!location && lines.length === 0) return undefined;
  return {
    ...(location ? { location_id: location } : {}),
    ...(lines.length > 0 ? { lines } : {}),
  };
}

/** One line's product, searched by name ("as sent" until one is picked). */
function LineProduct({
  label,
  value,
  refused = false,
  onPick,
}: {
  label: string;
  value: { id: string; name: string } | undefined;
  /** The server refused it for its product: say so. */
  refused?: boolean;
  onPick: (p: { id: string; name: string } | undefined) => void;
}) {
  const [q, setQ] = useState('');
  const deferred = useDeferredValue(q.trim());
  const results = useQuery({ ...productSearchQuery(deferred), enabled: deferred.length > 0 });
  return (
    <FormField
      label={label}
      description={
        value
          ? `Booked as ${value.name}.`
          : refused
            ? 'The product it names isn’t known here: type to pick the one it really is.'
            : 'As the station sent it. Type to pick another.'
      }
    >
      {(control) => (
        <div className="flex flex-col gap-2">
          <Input
            {...control}
            type="search"
            value={q}
            placeholder="Search the catalogue"
            onChange={(e) => setQ(e.target.value)}
          />
          {deferred.length > 0 && (
            <ul
              aria-label={`${label}: matching products`}
              className="flex max-h-40 flex-col overflow-y-auto rounded-md border border-border"
            >
              {(results.data?.items ?? []).map((p) => (
                <li key={p.id}>
                  <button
                    type="button"
                    onClick={() => {
                      onPick({ id: p.id, name: p.name });
                      setQ('');
                    }}
                    className="w-full px-3 py-2 text-left text-body text-fg hover:bg-surface-hover"
                  >
                    {p.name}
                  </button>
                </li>
              ))}
            </ul>
          )}
          {value && (
            <Button
              type="button"
              size="sm"
              variant="secondary"
              className="w-fit"
              onClick={() => onPick(undefined)}
            >
              Back to as sent
            </Button>
          )}
        </div>
      )}
    </FormField>
  );
}

/** Where it was put and what each line is, corrected before booking (D-066). */
/** Lot refusals a new lot fixes: the only ones the new-lot option is offered for. */
const LOT_REFUSALS = new Set(['lot_conflict', 'unknown_lot']);

function DeliveryCorrections({
  hold,
  refusal,
  onChange,
}: {
  hold: HeldCommandDetail;
  /** Why the server couldn't book it as sent (the uncorrected preview's refusal code). */
  refusal: string | undefined;
  onChange: (c: ReceiptCorrections | undefined) => void;
}) {
  const placeRefused = refusal === 'location_not_in_branch' || refusal === 'unknown_location';
  const lotRefused = refusal !== undefined && LOT_REFUSALS.has(refusal);
  const raw = hold.raw as { payload?: { lines?: DeliveryLine[] } } | null;
  const lines = raw?.payload?.lines ?? [];
  // Each line named by its product as sent, with its packs in words (UI6A-102): "Gluten-free
  // flour blend: 2 bags", not "Line 1: 2 bag". A product this account doesn't know keeps
  // its line number.
  const sentProducts = useQueries({
    queries: lines.map((l) => ({
      ...productQuery(l.product_id ?? ''),
      enabled: typeof l.product_id === 'string' && l.product_id.length > 0,
    })),
  });
  const locations = useQuery(locationsQuery());
  const [location, setLocation] = useState('');
  const [products, setProducts] = useState<Record<string, { id: string; name: string }>>({});
  const [newLots, setNewLots] = useState<Record<string, boolean>>({});
  useEffect(() => {
    onChange(correctionsOf(location, products, newLots));
  }, [location, products, newLots, onChange]);
  return (
    <section
      aria-labelledby="correct"
      className="flex flex-col gap-3 rounded-md border border-border p-3"
    >
      <h3 id="correct" className="text-sm font-semibold text-fg">
        Correct the delivery
      </h3>
      <p className="text-sm text-muted">
        The goods are there, so it is booked, not refused. Change what the server couldn’t book;
        what the station sent is kept with this action.
      </p>
      <FormField
        label="Put away at"
        description={
          placeRefused
            ? 'The place it names isn’t in the station’s branch: pick where it really went.'
            : undefined
        }
      >
        {(control) => (
          <Select
            value={location || 'as_sent'}
            onValueChange={(v) => setLocation(v === 'as_sent' ? '' : v)}
          >
            <SelectTrigger {...control}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="as_sent">As the station sent it</SelectItem>
              {(locations.data?.items ?? []).map((l) => (
                <SelectItem key={l.id} value={l.id}>
                  {l.branch.name}: {l.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </FormField>
      {lines.map((l, i) => {
        const sent = sentProducts[i]?.data;
        const label = `${sent?.name ?? `Line ${i + 1}`}: ${countedText(l.qty, l.uom, sent?.base_uom ?? '')}`;
        return (
          <div key={l.line_id} className="flex flex-col gap-2 border-t border-border pt-3">
            <LineProduct
              label={label}
              value={products[l.line_id]}
              refused={refusal === 'unknown_product'}
              onPick={(p) =>
                setProducts((all) => {
                  const next = { ...all };
                  if (p) next[l.line_id] = p;
                  else delete next[l.line_id];
                  return next;
                })
              }
            />
            {lotRefused && (
              <FormField
                label={`${label}: give it a new lot`}
                inline
                description={`${
                  refusal === 'lot_conflict'
                    ? `Its lot${l.lot?.lot_code ? ` ${l.lot.lot_code}` : ''} already belongs to another product.`
                    : `Its lot${l.lot?.lot_code ? ` ${l.lot.lot_code}` : ''} isn’t known here yet.`
                } A new lot keeps the printed code when it is free; print new labels afterwards.`}
              >
                {(control) => (
                  <Checkbox
                    {...control}
                    checked={newLots[l.line_id] === true}
                    onCheckedChange={(v) =>
                      setNewLots((all) => ({ ...all, [l.line_id]: v === true }))
                    }
                  />
                )}
              </FormField>
            )}
          </div>
        );
      })}
    </section>
  );
}

/** What applying left to do (the warnings the manager accepted), kept on the page (B-041). */
interface FollowUp {
  advice: string;
  /** The stock to look at: its product. */
  product: { id: string; name: string } | null;
}

function ApplyDialog({
  hold,
  onClose,
  onApplied,
  onDiscardInstead,
}: {
  hold: HeldCommandDetail;
  onClose: () => void;
  /** Applied with warnings: what is left to do, to show on the page after the toast goes. */
  onApplied?: (followUp: FollowUp) => void;
  /** Switch to discarding it, from the apply dialog (B-041). */
  onDiscardInstead?: () => void;
}) {
  const queryClient = useQueryClient();
  const timeZone = useAccountTimeZone();
  const [note, setNote] = useState('');
  const [checked, setChecked] = useState(false);
  const [ackError, setAckError] = useState<string>();
  const ackRef = useRef<HTMLButtonElement>(null);
  const ackBox = useRef<HTMLDivElement>(null);
  // Why it wasn't applied, in view with its box (the body may be scrolled).
  useEffect(() => {
    if (ackError) ackBox.current?.scrollIntoView({ block: 'nearest' });
  }, [ackError]);
  const delivery = hold.type === 'receipt.record';
  const [corrections, setCorrections] = useState<ReceiptCorrections>();
  const preview = useQuery(holdPreviewQuery(hold.id, corrections));
  const p = preview.data;
  // Why it can't be booked as sent, from the uncorrected preview: the corrections offered and
  // their words follow it, and the dialog leads to them instead of a dead end.
  const asSent = useQuery({
    ...holdPreviewQuery(hold.id),
    enabled: delivery && hold.physical === true,
  });
  const correctable =
    delivery && hold.physical === true && asSent.data?.outcome === 'still_rejected';
  const effective = p?.effective_at ?? hold.occurred_at;
  const dateLabel = effective ? formatDate(effective, { timeZone }) : null;
  const decide = useMutation({
    mutationFn: () =>
      resolveHold(hold.id, {
        action: 'apply',
        ...(note.trim() ? { note: note.trim() } : {}),
        ...(corrections ? { corrections } : {}),
      }),
    onSuccess: async () => {
      // The toast says what applying left behind (UI3A-203), from what the manager saw.
      const advice = appliedAdvice(p, timeZone);
      const risky = (p?.warnings ?? []).find(
        (w) => w.code === 'later_count' || w.code === 'below_zero_after',
      );
      const effect = risky?.effect === undefined ? undefined : p?.effects[risky.effect];
      await invalidateStations(queryClient);
      if (risky) {
        onApplied?.({
          advice,
          product: effect ? { id: effect.product.id, name: effect.product.name } : null,
        });
      }
      onClose();
      toast.success(dateLabel ? `Applied, dated ${dateLabel}` : 'Applied', {
        description: advice,
      });
    },
    onError: async () => {
      // The hold, or the stock it touches, may have changed: read it and its preview again
      // and show them as they are now (UI3A-202).
      await invalidateStations(queryClient);
    },
  });
  const failed = decide.isError ? applyFailure(decide.error) : null;
  // After a failure the old preview is stale until it has been read again.
  const checking = failed !== null && preview.isFetching;
  const applicable =
    p !== undefined && CAN_APPLY.has(p.outcome) && !checking && (!failed || failed.retry);
  const ack = p && applicable ? acknowledgement(p, timeZone) : null;
  // Refused, and the preview read again agrees it can't be applied: the failure says it all.
  const failureSaysIt = failed !== null && p !== undefined && !CAN_APPLY.has(p.outcome);
  const previewFailed = preview.isError
    ? problemWords(preview.error, 'Couldn’t check what it would change')
    : null;

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!applicable) return;
    if (ack && !checked) {
      setAckError('Tick this to apply it, or discard the action instead.');
      ackRef.current?.focus();
      return;
    }
    decide.mutate();
  };
  return (
    <DialogForm onSubmit={submit}>
      <DialogHeader>
        <DialogTitle>{delivery ? 'Book this delivery?' : 'Apply this action?'}</DialogTitle>
        <DialogDescription>
          {hold.physical
            ? 'It goes into the stock ledger dated when it happened, as the device it came from. It can’t be undone here; a correction would be a new action.'
            : 'It is recorded as if the station had sent it on time. It can’t be undone here.'}
        </DialogDescription>
      </DialogHeader>
      <DialogBody>
        <div className="flex flex-col gap-1 rounded-md bg-surface-sunken p-3">
          <p className="text-body font-medium text-fg">{whatHappened(hold)}</p>
          <p className="text-sm text-muted">
            {datedSentence(hold.occurred_at, hold.received_at, timeZone)}
          </p>
        </div>
        {failed && (
          <Notice tone="danger" alert title="Nothing was recorded" technical={failed.technical}>
            {failed.reason ? (
              <>
                <span className="font-medium">{failed.reason.title}.</span> {failed.reason.explain}{' '}
                It stays here: apply it once that is sorted out, or discard it with a note.
              </>
            ) : (
              <>
                {failed.title !== 'It wasn’t applied' && (
                  <span className="font-medium">{failed.title}. </span>
                )}
                {failed.explain}
              </>
            )}
          </Notice>
        )}
        {previewFailed ? (
          <Notice tone="danger" title={previewFailed.title} technical={previewFailed.technical}>
            {previewFailed.explain}
            {previewFailed.retry && (
              <Button
                type="button"
                size="sm"
                variant="secondary"
                className="mt-2 w-fit"
                icon={<RotateCcw aria-hidden />}
                onClick={() => void preview.refetch()}
              >
                Check again
              </Button>
            )}
          </Notice>
        ) : !p || checking ? (
          <div className="flex flex-col gap-2" aria-busy="true">
            <span role="status" className={checking ? 'text-sm text-muted' : 'sr-only'}>
              {checking ? 'Checking again what it would change…' : 'Checking what it would change…'}
            </span>
            <Skeleton className="h-4 w-32" />
            <Skeleton className="h-16 w-full" />
          </div>
        ) : failureSaysIt ? null : (
          <PreviewBody
            preview={p}
            correctable={correctable}
            ack={
              applicable && ack ? (
                <div ref={ackBox}>
                  <FormField label={ack} inline required error={ackError}>
                    {(control) => (
                      <Checkbox
                        {...control}
                        ref={ackRef}
                        checked={checked}
                        onCheckedChange={(v) => {
                          setChecked(v === true);
                          if (ackError) setAckError(undefined);
                        }}
                      />
                    )}
                  </FormField>
                </div>
              ) : undefined
            }
          />
        )}
        {correctable && (
          <DeliveryCorrections
            hold={hold}
            refusal={asSent.data?.refusal?.code}
            onChange={setCorrections}
          />
        )}
        {applicable && (
          <FormField label="Note (optional)" description="For example, who you checked it with.">
            <Textarea value={note} maxLength={500} onChange={(e) => setNote(e.target.value)} />
          </FormField>
        )}
      </DialogBody>
      <DialogFooter>
        {applicable || correctable ? (
          <>
            {/* Seen the preview and it shouldn’t go in: discard from here (B-041). */}
            {onDiscardInstead && (
              <Button
                type="button"
                variant="ghost"
                className="sm:mr-auto"
                icon={<Trash2 aria-hidden />}
                onClick={onDiscardInstead}
              >
                Discard instead…
              </Button>
            )}
            <Button type="button" variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              icon={failed?.retry ? <RotateCcw aria-hidden /> : <Check aria-hidden />}
              loading={decide.isPending}
              // A delivery that can't be booked as sent: only once a correction makes it bookable.
              disabled={!applicable}
            >
              {failed?.retry
                ? 'Try again'
                : correctable
                  ? 'Apply with corrections'
                  : dateLabel
                    ? `Apply, dated ${dateLabel}`
                    : 'Apply it'}
            </Button>
          </>
        ) : (
          <Button
            type="button"
            variant={p || failed || previewFailed ? 'primary' : 'secondary'}
            onClick={onClose}
          >
            Close
          </Button>
        )}
      </DialogFooter>
    </DialogForm>
  );
}

function DiscardDialog({ hold, onClose }: { hold: HeldCommandDetail; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [note, setNote] = useState('');
  const [error, setError] = useState<string>();
  const decide = useMutation({
    mutationFn: () => resolveHold(hold.id, { action: 'discard', note: note.trim() }),
    onSuccess: async () => {
      await invalidateStations(queryClient);
      onClose();
      toast.success('Discarded', {
        description: 'It won’t be applied. Your note is kept with it.',
      });
    },
    onError: async () => {
      await invalidateStations(queryClient);
    },
  });
  const failed = decide.isError ? problemWords(decide.error, 'It wasn’t discarded') : null;
  const closed = decide.error instanceof ApiError && decide.error.code === 'hold_already_resolved';
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!note.trim()) {
      setError('Say why it is discarded, for the record.');
      return;
    }
    decide.mutate();
  };
  return (
    <DialogForm onSubmit={submit}>
      <DialogHeader>
        <DialogTitle>Discard this action?</DialogTitle>
        <DialogDescription>
          It will never be applied. The action and your note are kept for the record, and the
          station that sent it shows your note.
        </DialogDescription>
      </DialogHeader>
      <DialogBody>
        <p className="rounded-md bg-surface-sunken p-3 text-body font-medium text-fg">
          {whatHappened(hold)}
        </p>
        <FormField
          label="Why"
          required
          error={error}
          description="For example, “Entered again at the station” or “Never happened”."
        >
          <Textarea
            value={note}
            maxLength={500}
            onChange={(e) => {
              setNote(e.target.value);
              if (error) setError(undefined);
            }}
          />
        </FormField>
        {failed && (
          <Notice tone="danger" alert title={failed.title} technical={failed.technical}>
            {failed.explain}
          </Notice>
        )}
      </DialogBody>
      <DialogFooter>
        {closed ? (
          <Button type="button" onClick={onClose}>
            Close
          </Button>
        ) : (
          <>
            <Button type="button" variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="destructiveSolid"
              icon={<Trash2 aria-hidden />}
              loading={decide.isPending}
            >
              Discard it
            </Button>
          </>
        )}
      </DialogFooter>
    </DialogForm>
  );
}

function DecisionDialog({
  hold,
  action,
  onClose,
  onApplied,
  onSwitch,
}: {
  hold: HeldCommandDetail;
  action: 'apply' | 'discard';
  onClose: () => void;
  onApplied: (followUp: FollowUp) => void;
  onSwitch: (action: 'apply' | 'discard') => void;
}) {
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent role="alertdialog" className="max-w-xl">
        {/* Rendered only while deciding, so each opening starts afresh. */}
        {action === 'apply' ? (
          <ApplyDialog
            hold={hold}
            onClose={onClose}
            onApplied={onApplied}
            onDiscardInstead={() => onSwitch('discard')}
          />
        ) : (
          <DiscardDialog hold={hold} onClose={onClose} />
        )}
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------- the page

/** A decided hold: who, when, the note, and what applying recorded (in words). */
function Decision({ hold }: { hold: HeldCommandDetail }) {
  const status = HOLD_STATUS[hold.status];
  const who = hold.resolved_by?.name ?? 'A manager';
  const result = hold.result;
  const refused = result?.status === 'rejected';
  const refusedWords = refused
    ? codeWords(result.reason, { title: 'Refused', explain: 'The server refused it.' })
    : null;
  return (
    <section
      aria-labelledby="decision"
      className="flex flex-col gap-3 rounded-lg border border-border bg-surface-raised p-5"
    >
      <div className="flex flex-wrap items-center gap-3">
        <h2 id="decision" className="font-display text-lg font-semibold text-fg">
          Decision
        </h2>
        <StatusBadge status={status.status}>{status.label}</StatusBadge>
      </div>
      <p className="text-body text-fg">
        {hold.status === 'discarded' ? 'Discarded' : 'Applied'} by {who}
        {hold.resolved_at && (
          <>
            {' '}
            on <AccountDate value={hold.resolved_at} withTime />
          </>
        )}
        .
      </p>
      {hold.resolution_note && (
        <blockquote className="border-l-2 border-border pl-3 text-body text-fg">
          “{hold.resolution_note}”
        </blockquote>
      )}
      {refusedWords && (
        <Notice
          tone="danger"
          title={`The server refused it: ${refusedWords.title.toLowerCase()}`}
          technical={[result?.reason, result?.message].filter(Boolean).join(': ') || null}
        >
          {refusedWords.explain} Nothing was recorded.
        </Notice>
      )}
      {result && result.flags.length > 0 && (
        <div className="flex flex-col gap-1">
          <p className="text-body text-fg">Recorded with notes for review:</p>
          <ul className="ml-5 list-disc text-body text-fg">
            {result.flags.map((f, i) => (
              <li key={`${f.code}-${i}`}>{flagWords(f.code)}</li>
            ))}
          </ul>
          <TechnicalDetail>
            {result.flags.map((f) => `${f.code}${f.message ? `: ${f.message}` : ''}`).join(' · ')}
          </TechnicalDetail>
        </div>
      )}
    </section>
  );
}

function WhyHeld({ hold }: { hold: HeldCommandDetail }) {
  const reason = HOLD_REASON[hold.reason];
  const open = hold.status === 'open';
  return (
    <section
      aria-labelledby="why"
      className={
        open
          ? 'rounded-lg border border-state-warning bg-state-warning-subtle p-5'
          : 'flex flex-col gap-1'
      }
    >
      <h2 id="why" className="font-display text-lg font-semibold text-fg">
        {open ? 'Why it’s held' : 'Why it was held'}: {reason.label.toLowerCase()}
      </h2>
      <p className="mt-1 text-body text-fg">{reason.explain}</p>
      {!hold.applicable && open && !DISCARD_ONLY_EXPLAINED.has(hold.reason) && (
        <p className="mt-2 text-body font-medium text-fg">
          This one can’t be applied; discard it with a note.
        </p>
      )}
      {hold.message && (
        <div className="mt-2">
          <TechnicalDetail>{hold.message}</TechnicalDetail>
        </div>
      )}
    </section>
  );
}

function Details({ hold }: { hold: HeldCommandDetail }) {
  const late = lateBy(hold.occurred_at, hold.received_at);
  const device = deviceOf(hold);
  // The app version the envelope carried, as on a rejected action (B-041).
  const appVersion = (hold.raw as { app_version?: unknown } | null)?.app_version;
  return (
    <section aria-labelledby="details" className="flex flex-col gap-3">
      <h2 id="details" className="font-display text-lg font-semibold text-fg">
        Details
      </h2>
      <dl className="flex flex-col gap-3">
        <Row label="Done by">{hold.staff?.display_name ?? 'Someone this account doesn’t know'}</Row>
        <Row label="Device">
          <DeviceName code={device.code} station={device.station} />
          {hold.device?.revoked && ' (revoked)'}
        </Row>
        {hold.device && hold.device.id !== hold.uploaded_by.id && (
          <Row label="Sent by">
            <DeviceName
              code={hold.uploaded_by.device_code}
              station={hold.uploaded_by.station_name}
            />
          </Row>
        )}
        <Row label="Happened">
          {hold.occurred_at ? (
            <AccountDate value={hold.occurred_at} withTime />
          ) : (
            <span className="text-muted">Not stated</span>
          )}
        </Row>
        <Row label="Reached the server">
          <AccountDate value={hold.received_at} withTime />
          {late !== null &&
            (late > LATE_MS ? (
              <Badge tone="warning" className="ml-2">
                {durationText(late)} later
              </Badge>
            ) : (
              <span className="text-muted"> ({durationText(late)} later)</span>
            ))}
        </Row>
        <Row label="Station app">
          {typeof appVersion === 'string' && appVersion ? (
            appVersion
          ) : (
            <span className="text-muted">Not stated</span>
          )}
        </Row>
      </dl>
      <details className="rounded-lg border border-border p-4">
        <summary className="w-fit cursor-pointer rounded-sm text-body font-medium text-fg">
          What the station sent
        </summary>
        <pre className="mt-3 overflow-x-auto whitespace-pre-wrap break-all font-mono text-xs text-fg">
          {JSON.stringify(hold.raw, null, 2)}
        </pre>
      </details>
    </section>
  );
}

function HeldActionPage() {
  const { holdId } = Route.useParams();
  const canManage = useCanManageDevices();
  const q = useQuery({ ...holdQuery(holdId), enabled: canManage === true });
  const hold = q.data;
  const title = hold ? commandLabel(hold.type) : 'Held action';
  useDocumentTitle(title);
  const [deciding, setDeciding] = useState<'apply' | 'discard' | null>(null);
  // What applying left to do: stays on the page, not only in the toast (B-041).
  const [followUp, setFollowUp] = useState<FollowUp | null>(null);

  const back = (
    <Button variant="secondary" asChild>
      <Link to="/settings/held-actions">
        <ArrowLeft aria-hidden />
        All held actions
      </Link>
    </Button>
  );

  if (canManage === false) {
    return (
      <SettingsFrame>
        <PageHeader title="Held action" />
        <NoDeviceAccess />
      </SettingsFrame>
    );
  }
  if (q.isError) {
    const notFound = q.error instanceof ApiError && q.error.status === 404;
    return (
      <SettingsFrame>
        {notFound ? (
          // Laid out as a found one (UI3A-213): the trail above, the way back below.
          <>
            <SettingsBreadcrumb
              trail={[{ to: '/settings/held-actions', label: 'Held station actions' }]}
              page="Not found"
            />
            <PageHeader
              title="This held action isn’t here"
              description="It may belong to another branch, or the link is wrong."
            />
            <div>{back}</div>
          </>
        ) : (
          <ErrorPanel
            error={q.error}
            what="This held action"
            onRetry={() => void q.refetch()}
            extraAction={back}
          />
        )}
      </SettingsFrame>
    );
  }
  if (!hold) {
    return (
      <SettingsFrame>
        {/* A page has its h1 while loading too, as count detail does (UI8B-7). */}
        <h1 className="sr-only">Loading the held action</h1>
        <LoadingSkeleton variant="page" label="Loading the held action" />
      </SettingsFrame>
    );
  }

  const open = hold.status === 'open';
  return (
    <SettingsFrame>
      <SettingsBreadcrumb
        trail={[{ to: '/settings/held-actions', label: 'Held station actions' }]}
        page={title}
      />
      <BackToOrder />
      <PageHeader
        title={title}
        description={whatHappened(hold)}
        actions={
          open ? (
            <>
              <Button
                variant="destructive"
                icon={<Trash2 aria-hidden />}
                onClick={() => setDeciding('discard')}
              >
                Discard
              </Button>
              {hold.applicable && (
                <Button icon={<Check aria-hidden />} onClick={() => setDeciding('apply')}>
                  Apply…
                </Button>
              )}
            </>
          ) : undefined
        }
      />
      <div className="flex flex-col gap-6">
        {open ? (
          <>
            <WhyHeld hold={hold} />
            <Details hold={hold} />
          </>
        ) : (
          <>
            {followUp && (
              <div data-testid="applied-follow-up">
                <Notice tone="warning" title="What to do now">
                  {followUp.advice}{' '}
                  {followUp.product && (
                    <Link
                      to="/stock/$productId"
                      params={{ productId: followUp.product.id }}
                      className="font-medium text-primary-text underline underline-offset-2"
                    >
                      See {followUp.product.name}’s stock and counts
                    </Link>
                  )}
                </Notice>
              </div>
            )}
            <Decision hold={hold} />
            <Details hold={hold} />
            <WhyHeld hold={hold} />
          </>
        )}
        <div>{back}</div>
      </div>
      {deciding && (
        <DecisionDialog
          // A fresh dialog when it switches from applying to discarding.
          key={deciding}
          hold={hold}
          action={deciding}
          onClose={() => setDeciding(null)}
          onApplied={setFollowUp}
          onSwitch={setDeciding}
        />
      )}
    </SettingsFrame>
  );
}
